import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Wand2, Copy, CheckCircle, Link2 } from 'lucide-react';

interface UTMModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateLink: (newUrl: string) => void;
  onCreateShortLink?: (url: string) => void;
  utmResponse: {
    final_url: string;
    utm_params: Record<string, string>;
  } | null;
}

export const UTMModal: React.FC<UTMModalProps> = ({
  isOpen,
  onClose,
  onUpdateLink,
  onCreateShortLink,
  utmResponse
}) => {
  const { t } = useTranslation();
  const [editableUtmParams, setEditableUtmParams] = useState<Record<string, string>>({});
  const [liveFinalUrl, setLiveFinalUrl] = useState('');
  const [copiedUtm, setCopiedUtm] = useState(false);

  // Initialize editable params when UTM response changes
  useEffect(() => {
    if (utmResponse) {
      setEditableUtmParams(utmResponse.utm_params);
      setLiveFinalUrl(utmResponse.final_url);
    }
  }, [utmResponse]);

  // Update live URL when params change
  useEffect(() => {
    if (utmResponse && Object.keys(editableUtmParams).length > 0) {
      const baseFinalUrl = utmResponse.final_url.split('?')[0];
      const newSearchParams = new URLSearchParams();
      
      for (const key in editableUtmParams) {
        if (Object.prototype.hasOwnProperty.call(editableUtmParams, key) && editableUtmParams[key]) {
          newSearchParams.append(key, editableUtmParams[key]);
        }
      }
      
      setLiveFinalUrl(`${baseFinalUrl}?${newSearchParams.toString()}`);
    } else if (utmResponse) {
      setLiveFinalUrl(utmResponse.final_url);
    }
  }, [editableUtmParams, utmResponse]);

  const handleUtmParamChange = (paramKey: string, value: string) => {
    setEditableUtmParams(prev => ({ ...prev, [paramKey]: value }));
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(liveFinalUrl);
    setCopiedUtm(true);
    setTimeout(() => setCopiedUtm(false), 2000);
  };

  const handleUpdateLinkClick = () => {
    onUpdateLink(liveFinalUrl);
    handleClose();
  };

  const handleCreateShortLinkClick = () => {
    if (onCreateShortLink) {
      onCreateShortLink(liveFinalUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    onClose();
    setEditableUtmParams({});
    setLiveFinalUrl('');
  };

  if (!isOpen || !utmResponse) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl border border-black min-h-[520px]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-primary-600" />
            {t('postContent.utmModal.title', 'Enhanced UTM link generated with AI')}
          </h3>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600"
            aria-label={t('common.close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-sm text-gray-600 mb-4">
          {t('postContent.utmModal.description', 'This smart link works just like your original one, but now you can track clicks in Google Analytics, for free.')}
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('postContent.utmModal.finalUrl', 'New Link with UTM:')}
            </label>
            <div className="relative">
              <textarea
                readOnly
                value={liveFinalUrl}
                className="w-full p-2 border rounded-md text-sm resize-none bg-primary-50 border-primary-600 text-gray-700 pr-10"
                rows={3}
              />
              <button
                type="button"
                onClick={handleCopyToClipboard}
                className="absolute top-2 right-2 p-1 text-primary-600 hover:text-primary-800 focus:outline-none"
                title={copiedUtm ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
              >
                {copiedUtm ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              {t('postContent.utmModal.utmParams', 'UTM Parameters:')}
            </label>
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50 space-y-2 max-h-none overflow-y-auto">
              {Object.entries(editableUtmParams).map(([key, value]) => (
                <div key={key} className="flex items-center">
                  <label 
                    htmlFor={`utm-${key}`} 
                    className="w-1/3 text-xs text-gray-600 truncate pr-2"
                  >
                    {key}:
                  </label>
                  <input 
                    type="text"
                    id={`utm-${key}`}
                    value={value}
                    onChange={(e) => handleUtmParamChange(key, e.target.value)}
                    className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-between gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('postContent.utmModal.keepOld', 'No, keep old link')}
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={handleUpdateLinkClick}
              className="px-4 py-2 text-sm font-medium text-primary-600 bg-white border border-primary-600 rounded-md hover:bg-primary-50"
            >
              {t('postContent.utmModal.updateLink', 'Yes, update link')}
            </button>
            {onCreateShortLink && (
              <button
                onClick={handleCreateShortLinkClick}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                <Link2 className="w-4 h-4" />
                {t('postContent.utmModal.createShortLink', 'Raccourcir le lien')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 